import { forwardRef } from "react";

import { Box, IconButton } from "@mui/material";
import { TimePicker as MUITimePicker } from "@mui/x-date-pickers/TimePicker";
import { gray } from "configs/colors";
import { shadows } from "configs/shadows";
import { rem } from "utils";

import { useTimePicker } from "./TimePicker.hooks";
import {
  TimePickerProps,
  TimePickerReference,
  TimePickerTextFieldProps,
} from "./TimePicker.types";
import { ClockIcon } from "../Icon";
import TextField from "../TextField/TextField";

const TimePickerTextField = ({
  InputProps,
  onClick,
  onChange,
  ...innerProps
}: TimePickerTextFieldProps) => {
  return (
    <TextField
      {...innerProps}
      onChangeEvent={onChange}
      onClick={onClick}
      InputProps={{
        ...(InputProps ?? {}),
        startAdornment: (
          <IconButton
            size="small"
            disabled={innerProps.disabled}
            sx={{ marginX: rem(-6) }}
            onClick={
              onClick as unknown as React.MouseEventHandler<HTMLButtonElement>
            }
          >
            <ClockIcon fontSize="small" sx={{ color: gray[700] }} />
          </IconButton>
        ),
        endAdornment: null, // Hide default "Calendar" icon of MUI Date Picker
      }}
    />
  );
};

const TimePicker = forwardRef<TimePickerReference, TimePickerProps>(
  (
    {
      defaultValue,
      value,
      placeholder,
      required,
      helperText,
      error,
      onChange,
      ...innerProps
    },
    ref
  ) => {
    const {
      rootRef,
      valueState,
      openCalendar,
      onChangeHandler,
      onCalendarCloseHandler,
      toggleCalendar,
    } = useTimePicker({
      ref,
      defaultValue,
      value,
      onChange,
    });

    return (
      <Box ref={rootRef}>
        <MUITimePicker
          ampm={false}
          value={valueState}
          open={openCalendar}
          onChange={onChangeHandler}
          onClose={onCalendarCloseHandler}
          slots={{ textField: TimePickerTextField }}
          slotProps={{
            popper: {
              sx: {
                ".MuiPickersPopper-paper": {
                  boxShadow: shadows.lg,
                },
              },
            },
            textField: {
              required,
              placeholder,
              helperText,
              error,
              onClick: toggleCalendar as unknown as never,
            },
          }}
          {...innerProps}
        />
      </Box>
    );
  }
);

export default TimePicker;
